import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ArrowLeft, BookOpen, Printer, Shield, Search, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../AuthContext';
import { openPrintWindow, buildPrintHTML } from '../../utils/printUtils';
import { exportToExcel } from '../../utils/excelExport';

const fmt = (n) => `Rs.${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const dateIN = (s) => (s ? new Date(s).toLocaleDateString('en-IN') : '-');

const MemberLedgerReport = () => {
  const { user } = useAuth();

  const [accounts, setAccounts] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/ledger/accounts').then(r => setAccounts(r.data || [])).catch(() => {});
  }, []);

  const filteredAccounts = accounts.filter(a => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (a.member_name || '').toLowerCase().includes(q) || (a.account_name || '').toLowerCase().includes(q);
  });

  const fetchLedger = async () => {
    if (!selectedId) { toast.error('Please select an account'); return; }
    setLoading(true);
    try {
      const params = {};
      if (startDate) params.start_date = `${startDate}T00:00:00Z`;
      if (endDate) params.end_date = `${endDate}T23:59:59Z`;
      const r = await api.get(`/ledger/accounts/${selectedId}/entries`, { params });
      setLedger(r.data);
    } catch { toast.error('Failed to load ledger'); }
    finally { setLoading(false); }
  };

  if (user && !['Admin', 'Account'].includes(user.role)) {
    return (
      <div className="p-8 text-center">
        <Shield className="w-16 h-16 mx-auto mb-4 text-red-300" />
        <h2 className="text-xl font-bold text-slate-900 mb-2">Access Restricted</h2>
        <p className="text-slate-500">This report is available only to Admin and Account roles.</p>
      </div>
    );
  }

  const handleExcel = () => {
    if (!ledger) return;
    const acc = ledger.account;
    const rows = (ledger.entries || []).map((e, i) => ({
      'SN': i + 1,
      'Date': dateIN(e.date),
      'Head': e.head_name,
      'Description': e.description || '',
      'Reference No.': e.reference_no || '',
      'Debit (Rs.)': e.debit_amount || 0,
      'Credit (Rs.)': e.credit_amount || 0,
      'Running Balance (Rs.)': Number((e.running_balance || 0).toFixed(2)),
    }));
    exportToExcel({
      filename: `WWAGS_Member_Ledger_${(acc.member_name || 'member').replace(/\s+/g,'_')}`,
      sheetName: 'Member Ledger',
      rows,
      kpis: [
        { label: 'Member', value: `${acc.member_name} (${acc.member_type})` },
        { label: 'Account', value: `${acc.account_name} (${acc.account_type})` },
        { label: 'Period', value: (startDate || endDate) ? `${startDate || 'Beginning'} to ${endDate || 'Today'}` : 'All Time' },
        { label: 'Total Debit', value: Number((ledger.total_debit || 0).toFixed(2)) },
        { label: 'Total Credit', value: Number((ledger.total_credit || 0).toFixed(2)) },
        { label: 'Closing Balance', value: `${Number(Math.abs(ledger.closing_balance || 0).toFixed(2))} ${ledger.closing_balance >= 0 ? 'Cr' : 'Dr'}` },
      ],
    });
  };

  const handlePrint = () => {
    if (!ledger) return;
    const acc = ledger.account;
    const rows = ledger.entries.map((e, i) => `<tr style="background:${i%2?'#f9f9f9':'#fff'}">
      <td>${dateIN(e.date)}</td><td>${e.head_name}</td><td>${e.description || '-'}</td>
      <td>${e.reference_no || '-'}</td>
      <td style="text-align:right">${e.debit_amount ? fmt(e.debit_amount) : '-'}</td>
      <td style="text-align:right">${e.credit_amount ? fmt(e.credit_amount) : '-'}</td>
      <td style="text-align:right;font-weight:bold">${fmt(e.running_balance)}</td>
    </tr>`).join('');

    const body = `
      <div class="info">
        <p><b>Member:</b> ${acc.member_name} (${acc.member_type})</p>
        <p><b>Account:</b> ${acc.account_name} (${acc.account_type})</p>
        ${(startDate || endDate) ? `<p><b>Period:</b> ${startDate || 'Beginning'} to ${endDate || 'Today'}</p>` : '<p><b>Period:</b> All Time</p>'}
      </div>
      <div class="cards">
        <div class="card" style="background:#fef2f2"><small>Total Debit</small><b>${fmt(ledger.total_debit)}</b></div>
        <div class="card" style="background:#ecfdf5"><small>Total Credit</small><b>${fmt(ledger.total_credit)}</b></div>
        <div class="card" style="background:#eff6ff"><small>Closing Balance</small><b>${fmt(Math.abs(ledger.closing_balance))} ${ledger.closing_balance >= 0 ? 'Cr' : 'Dr'}</b></div>
      </div>
      <table>
        <thead><tr><th>Date</th><th>Particulars (Head)</th><th>Description</th><th>Ref No.</th><th>Debit</th><th>Credit</th><th>Balance</th></tr></thead>
        <tbody>
          <tr style="background:#f0fdf4;font-weight:bold">
            <td colspan="4">Opening Balance (${ledger.opening_balance_type})</td>
            <td style="text-align:right">${ledger.opening_balance_type === 'debit' ? fmt(ledger.opening_balance) : '-'}</td>
            <td style="text-align:right">${ledger.opening_balance_type === 'credit' ? fmt(ledger.opening_balance) : '-'}</td>
            <td></td>
          </tr>
          ${rows}
          <tr class="total-row">
            <td colspan="4"><b>Totals</b></td>
            <td style="text-align:right"><b>${fmt(ledger.total_debit)}</b></td>
            <td style="text-align:right"><b>${fmt(ledger.total_credit)}</b></td>
            <td style="text-align:right"><b>${fmt(ledger.closing_balance)}</b></td>
          </tr>
        </tbody>
      </table>
    `;
    openPrintWindow(buildPrintHTML(`Member Ledger — ${acc.member_name} / ${acc.account_name}`, body, acc.branch_name || 'All Branches'));
  };

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="member-ledger-report-page">
      <div className="flex items-center gap-3 mb-6">
        <Link to="/reports" className="text-slate-500 hover:text-slate-700"><ArrowLeft className="w-5 h-5" /></Link>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><BookOpen className="w-5 h-5 text-emerald-700" /> Member Ledger Report</h1>
          <p className="text-sm text-slate-600">View and print any member or staff's ledger with date filters</p>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4 space-y-3">
        <div className="flex items-center gap-2">
          <Search className="w-4 h-4 text-slate-400" />
          <Input placeholder="Search member or account..." value={search} onChange={e => setSearch(e.target.value)} className="flex-1" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="md:col-span-2">
            <Label className="text-xs">Account *</Label>
            {filteredAccounts.length === 0 ? (
              <div className="border border-slate-200 rounded-md px-3 py-2 text-sm text-slate-500 bg-slate-50" data-testid="report-no-accounts">
                {accounts.length === 0
                  ? 'No ledger accounts exist yet. Create one in Member Ledger first.'
                  : 'No accounts match your search.'}
              </div>
            ) : (
              <Select value={selectedId} onValueChange={setSelectedId}>
                <SelectTrigger data-testid="report-account-select"><SelectValue placeholder="Choose member account" /></SelectTrigger>
                <SelectContent>
                  {filteredAccounts.map(a => (
                    <SelectItem key={a.id} value={a.id}>{a.member_name} — {a.account_name} ({a.entry_count || 0} entries)</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <div>
            <Label className="text-xs">From Date</Label>
            <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">To Date</Label>
            <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchLedger} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="load-ledger-btn">Load Ledger</Button>
          {ledger && <Button variant="outline" onClick={handlePrint} data-testid="print-report-btn"><Printer className="w-4 h-4 mr-1" />Print / PDF</Button>}
          {ledger && <Button variant="outline" onClick={handleExcel} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="excel-btn"><FileSpreadsheet className="w-4 h-4 mr-1" />Excel</Button>}
        </div>
      </div>

      {loading && <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-700"></div></div>}

      {ledger && !loading && (
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
            <div>
              <h2 className="text-lg font-bold">{ledger.account.member_name}</h2>
              <p className="text-sm text-slate-600">{ledger.account.account_name} <span className="text-xs bg-slate-100 ml-2 px-2 py-0.5 rounded capitalize">{ledger.account.account_type}</span></p>
            </div>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div><p className="text-[10px] uppercase text-slate-500">Debit</p><p className="font-bold text-red-700">{fmt(ledger.total_debit)}</p></div>
              <div><p className="text-[10px] uppercase text-slate-500">Credit</p><p className="font-bold text-emerald-700">{fmt(ledger.total_credit)}</p></div>
              <div><p className="text-[10px] uppercase text-slate-500">Balance</p><p className={`font-bold ${ledger.closing_balance >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{fmt(Math.abs(ledger.closing_balance))} {ledger.closing_balance >= 0 ? 'Cr' : 'Dr'}</p></div>
            </div>
          </div>
          <div className="overflow-x-auto border rounded">
            <table className="w-full text-xs">
              <thead className="bg-emerald-700 text-white"><tr>
                <th className="px-3 py-2 text-left">Date</th><th className="px-3 py-2 text-left">Head</th><th className="px-3 py-2 text-left">Description</th><th className="px-3 py-2 text-left">Ref No.</th><th className="px-3 py-2 text-right">Debit</th><th className="px-3 py-2 text-right">Credit</th><th className="px-3 py-2 text-right">Balance</th>
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                <tr className="bg-emerald-50 font-semibold">
                  <td colSpan="4" className="px-3 py-2">Opening ({ledger.opening_balance_type})</td>
                  <td className="px-3 py-2 text-right">{ledger.opening_balance_type === 'debit' ? fmt(ledger.opening_balance) : '-'}</td>
                  <td className="px-3 py-2 text-right">{ledger.opening_balance_type === 'credit' ? fmt(ledger.opening_balance) : '-'}</td>
                  <td></td>
                </tr>
                {ledger.entries.map((e, i) => (
                  <tr key={e.id} className={i % 2 ? 'bg-slate-50' : ''}>
                    <td className="px-3 py-2">{dateIN(e.date)}</td>
                    <td className="px-3 py-2 font-medium">{e.head_name}</td>
                    <td className="px-3 py-2 text-slate-600">{e.description || '-'}</td>
                    <td className="px-3 py-2 font-mono text-[10px]">{e.reference_no || '-'}</td>
                    <td className="px-3 py-2 text-right text-red-600">{e.debit_amount ? fmt(e.debit_amount) : '-'}</td>
                    <td className="px-3 py-2 text-right text-emerald-700">{e.credit_amount ? fmt(e.credit_amount) : '-'}</td>
                    <td className={`px-3 py-2 text-right font-bold ${e.running_balance >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>{fmt(e.running_balance)}</td>
                  </tr>
                ))}
                <tr className="bg-slate-100 font-bold">
                  <td colSpan="4" className="px-3 py-2">Totals</td>
                  <td className="px-3 py-2 text-right text-red-700">{fmt(ledger.total_debit)}</td>
                  <td className="px-3 py-2 text-right text-emerald-800">{fmt(ledger.total_credit)}</td>
                  <td className={`px-3 py-2 text-right ${ledger.closing_balance >= 0 ? 'text-emerald-800' : 'text-red-700'}`}>{fmt(ledger.closing_balance)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default MemberLedgerReport;
